import { PageOrThread } from '@/components/admin/experiences/conversation-thread';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { SimulationBar } from '@/components/admin/experiences/page-editor/simulation-bar';
import type { ReaderValue, ReaderValues } from '@/components/reader';
import {
  type Simulation,
  pageHasConditions,
  simulatedVariables,
  simulationSources,
} from '@/lib/display-conditions';
import {
  NO_RETAINED_NOYAU,
  type NoyauPreviewPair,
  type PreviewNoyauTexts,
  type PreviewRetained,
  noyauName,
  previewNoyau,
  previewNoyauOfCard,
  previewPairLine,
} from '@/lib/noyau-preview';
import { cn } from '@/lib/utils';
import type { VariableSources } from '@/lib/variable-pill';
import type {
  ExperienceBlockDraft,
  ExperiencePageDraft,
  ExperienceTheme,
  ExperienceVariable,
  NoyauCode,
} from '@matter_extractor/shared';
import { noyauOf } from '@matter_extractor/shared';
import { Button, Checkbox, Icon } from '@pms-digital/design-system';
import { Fragment, useState } from 'react';

/**
 * L'aperçu, sur le VRAI lecteur (refonte modulaire §9.3; planches
 * `EditeurPageEcran` et `EditeurPageTelephone`; issue #58).
 *
 * « Il défile dans son cadre quand la page est plus longue que lui, il réagit
 * au survol, au clic et à la saisie comme le lecteur, un bouton mène à l'aperçu
 * de la page suivante, et "Rejouer l'apparition" joue les temps et leurs
 * animations. » One component plays a page, and this is its third mount
 * (`components/reader/contracts.ts`): nothing about a page is drawn here.
 *
 * ## L'interrupteur choisit le MODE du lecteur, et rien d'autre
 *
 * « Sélection par l'aperçu » activée, the reader runs in `edit`: the page is
 * whole, a click on a block's visual selects it, and nothing inside a block
 * reacts. Deactivated, it runs in `interactive`: the temps play, a field takes
 * what is typed, a button advances. The switch is ON by default, which is the
 * posture the editor is opened in - one arrives to compose, not to rehearse.
 *
 * ## Les valeurs de l'aperçu ne quittent jamais l'écran
 *
 * What somebody types into the preview is a rehearsal and not content: it lives
 * in this component's state, keyed by block id like the reader's own contract,
 * and it is dropped when the page changes. Writing it into the draft would save
 * an operator's test answers into the experience everybody plays.
 *
 * ## Un bouton mène à l'aperçu de la page SUIVANTE, avec un retour
 *
 * §9.3 asks for it and the mockup draws the way back. It is preview state and
 * never navigation: the URL still names the page being EDITED, because the
 * stack and the inspector on either side are still that page's - and a preview
 * that moved the address would have silently changed what the save box saves.
 *
 * ## Tous les blocs par défaut, la page d'une personne sur demande (§9.2, H73)
 *
 * A page whose blocks carry display conditions gets the bar « Simuler » above
 * the frame (`simulation-bar.tsx`). « Montrer tous les blocs » is ON by default:
 * every block is drawn, and every conditional one wears its grey mark
 * (`conditionMark`). Turned off - or as soon as a simulated value is set - the
 * page is evaluated by `isBlockDisplayed` on the simulation, the function a
 * session plays, so the frame shows the page a person with those values would
 * see, and no mark: a person sees none. The simulation is posture, not content:
 * it lives here and dies with the page, like the values typed in the frame. The
 * NEXT page is shown whole and marked - the bar simulates the edited page.
 */

export interface PagePreviewProps {
  readonly page: ExperiencePageDraft;
  /** The next page, for the button that walks to it. Null on the last one. */
  readonly next: ExperiencePageDraft | null;
  readonly theme: ExperienceTheme;
  readonly format: 'phone' | 'screen';
  readonly selectedBlockId: string | null;
  readonly onSelectBlock: (blockId: string) => void;
  /** Bumped by « Rejouer l'apparition » and by every animation click (§9.3). */
  readonly replayKey: number;
  readonly onReplay: () => void;
  /** What a variable is NAMED here - the preview paints the pill, never a value (D5). */
  readonly variableNames: (variable: ExperienceVariable) => string;
  /** The pages of the experience, which a condition's Boutons are read off (§9.2). */
  readonly pages: readonly ExperiencePageDraft[];
  /** What names the variables of the bar « Simuler » - the screen's scope. */
  readonly sources: VariableSources;
  /** The grey mark of a conditional block, « si @noyau · SGP », or null (H73). */
  readonly conditionMark: (block: ExperienceBlockDraft) => string | null;
  /**
   * The texts of the noyaux the experience's reference reads (noyau.md §3.5),
   * null when there is none to read - no version, nothing under the tag: the
   * Noyau blocks then show « Aucun texte encore » (N20) - and undefined while
   * they are read, « Lecture des textes… ».
   */
  readonly noyauTexts: PreviewNoyauTexts;
  /** The pair of example « Aperçu avec » chose (N20). */
  readonly noyauPair: NoyauPreviewPair;
}

export function PagePreview({
  page,
  next,
  theme,
  format,
  selectedBlockId,
  onSelectBlock,
  replayKey,
  onReplay,
  variableNames,
  pages,
  sources,
  conditionMark,
  noyauTexts,
  noyauPair,
}: PagePreviewProps) {
  const [selectByPreview, setSelectByPreview] = useState(true);
  const [values, setValues] = useState<ReaderValues>({});
  /** Showing the NEXT page instead of the one being edited - see the head. */
  const [ahead, setAhead] = useState(false);
  /** « Montrer tous les blocs » and the values it would otherwise hide by (H73). */
  const [showAll, setShowAll] = useState(true);
  const [simulation, setSimulation] = useState<Simulation>({});
  /** The noyau of the card taken on a first choice of this preview, for the page it leads to. */
  const [taken, setTaken] = useState<NoyauCode | null>(null);

  const shown = ahead && next !== null ? next : page;
  const mode = ahead ? 'interactive' : selectByPreview ? 'edit' : 'interactive';
  const simulated = simulatedVariables(page, pages);
  const simulating = !ahead && !showAll && pageHasConditions(page);
  const conditions = simulating ? simulationSources(simulation, simulated, pages) : null;

  /*
    The Noyau blocks of the page show the REAL texts of the pair of example
    (noyau.md N20). The retained noyau of the simulation is the principal of
    that pair, unless the bar « Simuler » fixes one - the portrait then follows
    the images it unmasks - or a card was taken on the page the preview left.
    « Simuler : Aucun » is no noyau at all, the sequence skipped: nothing of
    any noyau is shown (`NO_RETAINED_NOYAU`).
  */
  const simulatesNoyau = simulated.some((variable) => variable.kind === 'noyau');
  const retained: PreviewRetained = ahead
    ? taken
    : conditions !== null && simulatesNoyau
      ? (conditions.noyau ?? NO_RETAINED_NOYAU)
      : null;
  const noyau = previewNoyau(shown, noyauTexts, noyauPair, retained);
  const pairLine = previewPairLine(shown, noyauPair, retained);

  function onValueChange(blockId: string, value: ReaderValue): void {
    setValues((was) => ({ ...was, [blockId]: value }));
  }

  return (
    <section data-testid={PAGE_EDITOR_IDS.preview} className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Checkbox
          label="Sélection par l’aperçu"
          description={
            ahead
              ? 'aperçu de la page suivante'
              : selectByPreview
                ? 'activée : un clic sur un bloc le sélectionne'
                : 'désactivée : la page réagit comme dans le lecteur'
          }
          data-testid={PAGE_EDITOR_IDS.previewSelect}
          checked={selectByPreview}
          disabled={ahead}
          onCheckedChange={setSelectByPreview}
        />

        <Button
          type="button"
          variant="accent"
          size="sm"
          icon="restart_alt"
          data-testid={PAGE_EDITOR_IDS.previewReplay}
          onClick={onReplay}
        >
          Rejouer l’apparition
        </Button>
      </div>

      {!ahead && pageHasConditions(page) ? (
        <SimulationBar
          variables={simulated}
          simulation={simulation}
          showAll={showAll}
          sources={sources}
          onSimulation={(next) => {
            setSimulation(next);
            setShowAll(false);
          }}
          onShowAll={setShowAll}
        />
      ) : null}

      {pairLine === null ? null : (
        /* « La paire d'aperçu d'un bloc Noyau se lit dans une ligne discrète
           au-dessus de l'aperçu » (maquette noyau README) - its colours are
           the operator's, never the person's (N19). */
        <p
          data-testid={PAGE_EDITOR_IDS.noyauPreviewPair}
          className="m-0 flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-gray-700 text-caption"
        >
          <span className="inline-flex shrink-0 text-gray-600">
            <Icon name="visibility" size={14} />
          </span>
          {/* The spaces are in the TEXT and not only in the gap: the line is a
              sentence, and a screen reader reads « la paire Souverain Magicien
              / Souverain Juriste », never « paireSouverain ». */}
          <span>{pairLine.lead}</span>{' '}
          {pairLine.noyaux.map((code, index) => (
            <Fragment key={code}>
              {index > 0 ? <span>{' / '}</span> : null}
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="inline-block size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: noyauOf(code).color }}
                />
                <span className="font-semibold text-gray-800">{noyauName(code)}</span>
              </span>
            </Fragment>
          ))}
        </p>
      )}

      {ahead ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-bleu-nuit-tint px-3 py-1.5">
          <span className="min-w-0 truncate font-semibold text-bleu-nuit text-caption">
            Aperçu de « {shown.name || 'Page sans nom'} »
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon="arrow_back"
            data-testid={PAGE_EDITOR_IDS.previewBack}
            onClick={() => setAhead(false)}
          >
            Revenir à la page éditée
          </Button>
        </div>
      ) : null}

      {/*
        Le cadre DÉFILE (§9.3, seconde passe) : la page est plus longue que lui
        dès qu'elle a quelques blocs, et l'aperçu doit se lire en entier sans
        allonger l'écran de l'éditeur, où la pile et l'inspecteur l'encadrent.
      */}
      <div
        className={cn(
          'mx-auto max-h-[38rem] overflow-y-auto overflow-x-hidden rounded-md border-2 border-gray-200 bg-white',
          format === 'phone' ? 'w-full max-w-[390px]' : 'w-full',
        )}
      >
        <PageOrThread
          key={`${shown.id}-${mode}`}
          page={shown}
          theme={theme}
          format={format}
          mode={mode}
          values={values}
          selectedBlockId={mode === 'edit' ? selectedBlockId : null}
          onSelectBlock={onSelectBlock}
          onValueChange={onValueChange}
          onAdvance={(advance) => {
            if (next === null) return;
            // « Choisir ce texte » leads to the next page like a Bouton; the
            // noyau of a first choice's card is the next page's retained one.
            if (advance.reason === 'noyau' && advance.block.mode === 'first-choice') {
              setTaken(previewNoyauOfCard(advance.cardId));
            }
            setAhead(true);
          }}
          replayKey={replayKey}
          variableNames={variableNames}
          noyau={noyau}
          {...(conditions === null ? { conditionMark } : { conditions })}
        />
      </div>

      <p
        {...(next === null ? {} : { 'data-testid': PAGE_EDITOR_IDS.previewNext })}
        className="text-gray-600 text-caption leading-4"
      >
        {next === null
          ? 'Dernière page : un bouton n’a rien vers quoi mener.'
          : `Un bouton mène à l’aperçu de la page suivante (${next.name || 'Page sans nom'}), et un retour ramène ici.`}
      </p>
    </section>
  );
}
